import React, { useState } from 'react';
import { Calculator, Package, Layers, TrendingUp } from 'lucide-react';
import { pricingService } from '../../services/pricingService';
import './PricingCalculator.css';

const PricingCalculator = ({ tiers, adjustments }) => {
  const [projectType, setProjectType] = useState('Typical Surface Model');
  const [projectSize, setProjectSize] = useState('');
  const [hasUtilities, setHasUtilities] = useState(false);
  const [hasErosion, setHasErosion] = useState(false);
  const [hasAdvUtilities, setHasAdvUtilities] = useState(false);
  const [advUtilityCount, setAdvUtilityCount] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const handleCalculate = async () => {
    if (projectType === 'Linework' || (projectSize && parseFloat(projectSize) > 0)) {
      try {
        setCalculating(true);
        const result = await pricingService.calculateProjectPrice(
          projectType,
          projectType === 'Linework' ? 0 : parseFloat(projectSize),
          {
            hasUtilities,
            hasErosion,
            hasAdvUtilities,
            advUtilityCount: parseInt(advUtilityCount) || 0
          }
        );
        setCalculatedPrice(result);
      } catch (error) {
        console.error('Error calculating price:', error);
        alert('Failed to calculate price');
      } finally {
        setCalculating(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const reset = () => {
    setProjectType('Typical Surface Model');
    setProjectSize('');
    setHasUtilities(false);
    setHasErosion(false);
    setHasAdvUtilities(false);
    setAdvUtilityCount('');
    setCalculatedPrice(null);
  };

  return (
    <div className="pricing-calculator">
      <div className="calculator-container">
        <div className="calculator-header">
          <h2>
            <Calculator size={24} />
            Project Pricing Calculator
          </h2>
          <p>Calculate project pricing using current database values</p>
        </div>

        <div className="calculator-form">
          <div className="form-section">
            <h3>Project Details</h3>
            
            <div className="form-group">
              <label>Project Type</label>
              <select 
                value={projectType} 
                onChange={(e) => setProjectType(e.target.value)}
              >
                <option value="Typical Surface Model">Typical Surface Model</option>
                <option value="Linework">Linework Only</option>
              </select>
            </div>

            {projectType !== 'Linework' && (
              <div className="form-group">
                <label>Project Size (Acres)</label>
                <input
                  type="number"
                  value={projectSize}
                  onChange={(e) => setProjectSize(e.target.value)}
                  placeholder="Enter size in acres (max 150)"
                  min="0.1"
                  max="150"
                  step="0.1"
                />
                {parseFloat(projectSize) > 150 && (
                  <div style={{color: 'red', fontSize: '12px', marginTop: '4px'}}>
                    Maximum project size is 150 acres. Please contact us for larger projects.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Optional Add-ons</h3>
            
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={hasErosion}
                  onChange={(e) => setHasErosion(e.target.checked)}
                />
                <Layers size={18} />
                <span>Erosion Surface</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={hasUtilities}
                  onChange={(e) => setHasUtilities(e.target.checked)}
                  disabled={projectType === 'Linework'}
                />
                <TrendingUp size={18} />
                <span>Elevated Utilities (+10% of base)</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={hasAdvUtilities}
                  onChange={(e) => setHasAdvUtilities(e.target.checked)}
                />
                <Package size={18} />
                <span>Advanced Utility Model</span>
              </label>
            </div>

            {hasAdvUtilities && (
              <div className="form-group">
                <label>Number of Structures</label>
                <input
                  type="number"
                  value={advUtilityCount}
                  onChange={(e) => setAdvUtilityCount(e.target.value)}
                  placeholder="Enter number of structures"
                  min="1"
                />
              </div>
            )}
          </div>

          <div className="calculator-actions">
            <button 
              className="reset-button"
              onClick={reset}
            >
              Reset
            </button>
            <button 
              className="calculate-button"
              onClick={handleCalculate}
              disabled={calculating || (projectType !== 'Linework' && (!projectSize || parseFloat(projectSize) <= 0))}
            >
              {calculating ? 'Calculating...' : 'Calculate Price'}
            </button>
          </div>
        </div>

        {calculatedPrice && (
          <div className="calculator-results">
            <h3>Pricing Breakdown</h3>
            
            <div className="result-items">
              <div className="result-item">
                <span className="result-label">
                  Base Cost
                  {projectType !== 'Linework' && ` (${projectSize} acres)`}
                </span>
                <span className="result-value">
                  {formatCurrency(calculatedPrice.base_cost)}
                </span>
              </div>

              {calculatedPrice.utility_cost > 0 && (
                <div className="result-item">
                  <span className="result-label">Elevated Utilities (10%)</span>
                  <span className="result-value">
                    {formatCurrency(calculatedPrice.utility_cost)}
                  </span>
                </div>
              )}

              {calculatedPrice.erosion_cost > 0 && (
                <div className="result-item">
                  <span className="result-label">Erosion Surface</span>
                  <span className="result-value">
                    {formatCurrency(calculatedPrice.erosion_cost)}
                  </span>
                </div>
              )}

              {calculatedPrice.advanced_utility_cost > 0 && (
                <div className="result-item">
                  <span className="result-label">
                    Advanced Utilities ({advUtilityCount} structures)
                  </span>
                  <span className="result-value">
                    {formatCurrency(calculatedPrice.advanced_utility_cost)}
                  </span>
                </div>
              )}

              <div className="result-item total">
                <span className="result-label">Total Project Cost</span>
                <span className="result-value">
                  {formatCurrency(calculatedPrice.total_cost)}
                </span>
              </div>
            </div>

            <div className="result-note">
              <p>
                This calculation uses the current pricing configuration from the database.
                Final pricing may vary based on project specifics.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingCalculator;